import type { BiomassDataRow } from './productionUnitTable'
import type { FishStockLog, MortalityLog, WeightSampleLog } from '../types/production'
import { overlayMortalityLogsOnProductionRows } from '../screens/biomass/mortalityLogBridge'
import { overlayWeightSamplesOnProductionRows } from '../screens/biomass/weightSampleLogBridge'
import { overlayFishStockOnProductionRows } from '../screens/biomass/fishStockLogBridge'
import { computeDisplayedEndOfDayStockByRowId } from './productionEndOfDayStock'
import { scopeLogsToLotBucket } from './biomassLotScoping'
import { dropEmptySameDayDuplicateRows } from './emptyDayRows'

/**
 * v2.2 T3-B-06e: canonical "current biomass per lot" derivation, shared
 * across any surface (Reports, Dashboard, Feed, Biomass) that needs the
 * live, overlay-derived biomass kg of an active lot.
 *
 * Extracted from the inline calculations duplicated in `Feed.tsx` and
 * `Biomass.tsx` (see [docs/architecture-concerns.md](../../docs/architecture-concerns.md)
 * invariant I-6 extended to "one source of truth per KPI"). The overlay
 * chain (`overlayMortality → overlayWeightSamples → overlayFishStock`)
 * matches the one Feed / Biomass / Dashboard already use, then the
 * end-of-day stock derivation (`computeDisplayedEndOfDayStockByRowId`)
 * fills in stock from mortality when a row's stored `fishStock` cell is
 * blank (post-T3-A-07b the retired `ProductionRecord.fishStock` cache
 * often leaves rows with blank stored stock).
 *
 * Pure, side-effect free, deterministic. Callers inject inputs; there
 * are no Dexie reads inside.
 */

export type CurrentBiomassPerLotEntry = {
  lotId: string
  biomassKg: number | null
  sourceWeightG: number | null
  sourceStockCount: number | null
  /** ISO date of the row that contributed the biomass figure, or null when no attributable row exists. */
  asOfDate: string | null
}

const parseNumeric = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed) return null
  const parsed = Number.parseFloat(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

const compareByDateDescThenIdAsc = (left: BiomassDataRow, right: BiomassDataRow): number => {
  const leftTime = Date.parse(String(left.date ?? ''))
  const rightTime = Date.parse(String(right.date ?? ''))
  const leftValid = Number.isFinite(leftTime)
  const rightValid = Number.isFinite(rightTime)

  if (leftValid && rightValid && leftTime !== rightTime) return rightTime - leftTime
  if (leftValid && !rightValid) return -1
  if (!leftValid && rightValid) return 1

  return String(left.id ?? '').localeCompare(String(right.id ?? ''))
}

const bucketContextKey = (row: BiomassDataRow): string => {
  const farmId = String((row as { farmId?: string }).farmId ?? '')
  const productionUnitId = String((row as { productionUnitId?: string }).productionUnitId ?? '')
  return `${farmId}::${productionUnitId}`
}

/**
 * Compute the current biomass kg for each lot referenced by the input
 * production rows.
 *
 * Keying: attribution is by `row.lotId`. Rows without a `lotId` are
 * skipped (unattributable). T3-B-06a's write-path fix ensures all
 * post-2026-07 rows carry `lotId`; historical rows without it would
 * need a `(farmId, lotKey) → lotId` backfill to appear here.
 *
 * `toRecordedAt`: optional inclusive-upper-bound YYYY-MM-DD (calendar
 * day) or ISO instant. When provided:
 *
 *   - Production rows with `date > toRecordedAt` are excluded (so
 *     "current" respects the report's as-of window).
 *   - Overlay logs with `recordedAt > toRecordedAt` are excluded, so
 *     mortality / weight / fish-stock events that happened AFTER the
 *     cutoff don't leak into the derivation.
 *
 * When omitted, the latest event of each type overlays regardless of
 * date (matches Feed / Biomass live behavior).
 */
export const computeCurrentBiomassPerLot = (args: {
  productionRows: readonly BiomassDataRow[]
  mortalityLogs: readonly MortalityLog[]
  weightSampleLogs: readonly WeightSampleLog[]
  fishStockLogs: readonly FishStockLog[]
  toRecordedAt?: string
}): Map<string, CurrentBiomassPerLotEntry> => {
  const result = new Map<string, CurrentBiomassPerLotEntry>()

  const toDate = args.toRecordedAt?.trim()
  // v2.2 findings.md #34: always truncate to the cutoff's own calendar day
  // before expanding to end-of-day, regardless of whether the caller passed
  // a bare `YYYY-MM-DD` or a full ISO timestamp (Biomass.tsx/Feed.tsx pass
  // `row.date`, which always carries a time-of-day). Previously a full
  // timestamp was used as the exact cutoff instant, which — combined with
  // `filteredRows` below already comparing each row's own end-of-day instant
  // — made a row exclude ITSELF from its own "as of this row's date" query
  // whenever its recorded time wasn't literal midnight. Mirrors the
  // already-correct `toDayEndMs` in the API-side sibling implementation
  // (packages/api/src/utils/currentBiomassPerLot.ts).
  const resolveCutoffMs = (raw: string | undefined): number => {
    if (!raw) return Number.POSITIVE_INFINITY
    const day = raw.slice(0, 10)
    if (!day) return Number.NaN
    return new Date(`${day}T23:59:59.999Z`).getTime()
  }
  const toDateMs = resolveCutoffMs(toDate)

  // Filter overlays by recordedAt bound so events after the cutoff don't
  // contaminate the derivation. Production rows filter by `date` (their
  // own calendar-day field, mirroring how Biomass/Feed treat them).
  const isAfterCutoff = (recordedAt: string | undefined): boolean => {
    if (!recordedAt || Number.isNaN(toDateMs)) return false
    if (toDateMs === Number.POSITIVE_INFINITY) return false
    const ms = new Date(recordedAt).getTime()
    return Number.isFinite(ms) && ms > toDateMs
  }

  const filteredMortality = args.mortalityLogs.filter((log) => !isAfterCutoff(log.recordedAt))
  const filteredWeight = args.weightSampleLogs.filter((log) => !isAfterCutoff(log.recordedAt))
  const filteredFishStock = args.fishStockLogs.filter((log) => !isAfterCutoff(log.recordedAt))

  const filteredRows = args.productionRows.filter((row) => {
    if (!toDate) return true
    const rowDay = String(row.date ?? '').slice(0, 10)
    if (!rowDay) return true
    const rowMs = new Date(`${rowDay}T23:59:59.999Z`).getTime()
    return Number.isFinite(rowMs) && rowMs <= toDateMs
  })

  // Bucket rows by (farmId, productionUnitId) so the overlay chain sees
  // only its own context's rows (overlay match keys are `(row.date,
  // row.lot)` which collide across contexts).
  const buckets = new Map<string, {
    farmId: string
    productionUnitId: string
    rows: BiomassDataRow[]
    lotIds: Set<string>
  }>()
  const attributableLotIds = new Set<string>()
  filteredRows.forEach((row) => {
    const key = bucketContextKey(row)
    const farmId = String((row as { farmId?: string }).farmId ?? '')
    const productionUnitId = String((row as { productionUnitId?: string }).productionUnitId ?? '')
    let bucket = buckets.get(key)
    if (!bucket) {
      bucket = { farmId, productionUnitId, rows: [], lotIds: new Set<string>() }
      buckets.set(key, bucket)
    }
    bucket.rows.push(row)

    const rowLotId = String(row.lotId ?? '').trim()
    if (rowLotId) {
      bucket.lotIds.add(rowLotId)
      attributableLotIds.add(rowLotId)
    }
  })

  // Run the overlay chain per bucket + derive end-of-day stock.
  const overlaidRowsByLotId = new Map<string, BiomassDataRow[]>()
  const derivedStockByRowId = new Map<string, { numericValue: number | null }>()

  buckets.forEach((bucket) => {
    // A1.1 Risk #2 fix: route logs to buckets by lotId when a production
    // row claims it, location only as fallback. Without this a
    // transferred lot's logs never reached the bucket holding its
    // pre-transfer rows, so those rows' weight/stock cells stayed at
    // their raw stored values and the carry-forward walk below resolved
    // stale figures. See `scopeLogsToLotBucket`.
    const scopedMortality = scopeLogsToLotBucket(filteredMortality, bucket, attributableLotIds)
    const scopedWeight = scopeLogsToLotBucket(filteredWeight, bucket, attributableLotIds)
    const scopedFishStock = scopeLogsToLotBucket(filteredFishStock, bucket, attributableLotIds)

    const overlaid = overlayFishStockOnProductionRows(
      overlayWeightSamplesOnProductionRows(
        overlayMortalityLogsOnProductionRows(dropEmptySameDayDuplicateRows(bucket.rows), scopedMortality),
        scopedWeight
      ),
      scopedFishStock
    )

    // computeDisplayedEndOfDayStockByRowId groups its input by
    // (lotId | lotKey) — safe here because we're already filtered to
    // one context and the returned Map keys by rowId globally.
    const derived = computeDisplayedEndOfDayStockByRowId(overlaid)
    derived.forEach((value, rowId) => derivedStockByRowId.set(rowId, value))

    overlaid.forEach((row) => {
      const lotId = typeof row.lotId === 'string' ? row.lotId.trim() : ''
      if (!lotId) return
      const list = overlaidRowsByLotId.get(lotId)
      if (list) {
        list.push(row)
      } else {
        overlaidRowsByLotId.set(lotId, [row])
      }
    })
  })

  overlaidRowsByLotId.forEach((rows, lotId) => {
    if (rows.length === 0) return

    // A1.1 fix (docs/architecture-concerns.md §A1.1, Hallazgo 2c): a lot
    // that has been transferred/moved has rows split across MULTIPLE
    // (farmId, productionUnitId) buckets (its pre-move rows in the old
    // bucket, post-move rows in the new one — see the bucketing loop
    // above). The single latest-dated row across ALL of a lot's buckets
    // used to be read directly for weight/stock, but that row's cells
    // only ever got filled by ITS OWN bucket's overlay pass (bucket
    // overlays are scoped by `(farmId, productionUnitId)` because the
    // overlay bridges match by `(date, lot label)`, which collides
    // across farms/production-units otherwise). If no weight sample
    // has been taken yet in the new location, the winning row's weight
    // cell comes back blank even though a legitimate last-known value
    // exists in the lot's pre-move history — silently zeroing biomass
    // right after every transfer.
    //
    // Fix: walk the lot's full row history (across all of its buckets,
    // newest-first) and resolve weight/stock independently, each from
    // the most recent row that actually carries a value for that field.
    // `asOfDate` reflects whichever of the two contributing rows is more
    // recent, so a report can still tell the reader "this is as fresh as
    // of X" even when weight and stock came from different rows/buckets.
    const sorted = [...rows].sort(compareByDateDescThenIdAsc)

    let weight: number | null = null
    let weightDate: string | null = null
    let stock: number | null = null
    let stockDate: string | null = null

    for (const row of sorted) {
      const rowDate = typeof row.date === 'string' && row.date ? row.date : null

      if (weight === null) {
        const candidateWeight = parseNumeric(row.weight)
        if (candidateWeight !== null && candidateWeight > 0) {
          weight = candidateWeight
          weightDate = rowDate
        }
      }

      if (stock === null) {
        // v2.2 findings.md #34: defer entirely to `computeDisplayedEndOfDayStockByRowId`'s
        // already-canonical value rather than re-deriving a second, conflicting
        // "raw stored value wins" precedence here — that duplicate rule is what let
        // a receiving day's un-netted raw count (e.g. 10000) win over the correctly
        // mortality-netted value (e.g. 9400) computed just above. `derivedStockByRowId`
        // already returns the raw stored value verbatim for a routine recount and the
        // netted value for a receiving event, so there is nothing left to prefer.
        const candidateStock = derivedStockByRowId.get(String(row.id ?? ''))?.numericValue ?? null
        if (candidateStock !== null && candidateStock > 0) {
          stock = candidateStock
          stockDate = rowDate
        }
      }

      if (weight !== null && stock !== null) break
    }

    const asOfDate = [weightDate, stockDate]
      .filter((value): value is string => Boolean(value))
      .sort()
      .pop()
      ?? (typeof sorted[0]?.date === 'string' && sorted[0].date ? sorted[0].date : null)

    const biomassKg = (weight !== null && weight > 0 && stock !== null && stock > 0)
      ? (stock * weight) / 1000
      : null

    result.set(lotId, {
      lotId,
      biomassKg,
      sourceWeightG: weight,
      sourceStockCount: stock,
      asOfDate
    })
  })

  return result
}
